import { isEmpty } from "lodash";
import { makeAutoObservable, runInAction } from "mobx";
import * as yup from "yup";

import { handleChange, HandleChangeOptions } from "@/utils/hookForm";

export default class FormState<T = Record<string, unknown>> {
  schema: yup.Schema<T>;
  errors: Partial<Record<keyof T | "base", string | undefined>> = {};
  isSubmitting = false;

  private object: any;
  private dirty: Partial<Record<keyof T, boolean>> = {};
  private touched: Partial<Record<keyof T, boolean>> = {};
  private options: Options = {
    validateOnChange: false,
    validateOnBlur: true,
  };

  get isValid(): boolean {
    return isEmpty(this.errors);
  }

  get isInvalid(): boolean {
    return !this.isValid;
  }

  get isDirty(): boolean {
    return !isEmpty(this.dirty);
  }

  constructor(
    object: any,
    schema: yup.Schema<T>,
    options: Partial<Options> = {}
  ) {
    makeAutoObservable(this);
    this.object = object;
    this.schema = schema;
    Object.assign(this.options, options);
  }

  reset() {
    this.isSubmitting = false;
    this.errors = {};
    this.dirty = {};
    this.touched = {};
  }

  async validate() {
    try {
      this.errors = {};
      await this.schema.validate(this.object, { abortEarly: false });
      return true;
    } catch (e) {
      const error = e as yup.ValidationError;
      error.inner.forEach((err) => {
        this.errors[(err.path as keyof T) || "base"] = err.message;
      });
      return false;
    }
  }

  field(name: keyof T, options: HandleChangeOptions = {}) {
    return {
      name,
      value: this.object[name],
      onChange: this.handleChange(name, options),
      onBlur: this.handleBlur(name),
    };
  }

  fieldError(name: keyof T) {
    return {
      error: !!this.errors[name],
      helperText: this.errors[name] || " ",
    };
  }

  handleChange(name: keyof T, options?: HandleChangeOptions) {
    return handleChange((value) => {
      runInAction(() => {
        this.object[name] = value;
        this.dirty[name] = true;
        if (this.options.validateOnChange) this.validateOnly(name);
        if (this.touched) this.validateOnly(name);
      });
    }, options);
  }

  handleBlur(name: keyof T) {
    return () => {
      runInAction(() => {
        this.touched[name] = true;
        if (this.options.validateOnBlur) this.validateOnly(name);
      });
    };
  }

  handleSubmit(handler: () => void | Promise<void>) {
    return () => {
      this.isSubmitting = true;
      this.validate()
        .then((isValid) => {
          if (isValid) return handler();
        })
        .finally(() => (this.isSubmitting = false));
    };
  }

  private async validateOnly(name: keyof T) {
    try {
      delete this.errors["base"];
      delete this.errors[name];
      // This will still validate ALL fields in order to include "base" in
      // validation.
      await this.schema.validate(this.object, { abortEarly: false });
      return true;
    } catch (e) {
      const error = e as yup.ValidationError;
      error.inner.forEach((err) => {
        if (err.path === name) {
          this.errors[err.path as keyof T] = err.message;
        }
        if (err.path === undefined) {
          this.errors["base"] = err.message;
        }
      });
      return false;
    }
  }
}

interface Options {
  validateOnChange: boolean;
  validateOnBlur: boolean;
}
